import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  CircularProgress,
  Icon,
} from "@mui/material";
import { PhotoCamera, Delete } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../api";
import { CustomModal } from "../components/CustomModal";

const createEmptyItem = () => ({ status: "", jumlah: "", keterangan: "" });

const useInitEquipment = (len) =>
  Array.from({ length: len }, () => createEmptyItem());

// New Component for Equipment List
const EquipmentBlock = ({ droneKey, labels, formData, onStatusChange }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
      {droneKey === "M3"
        ? "DJI M3 ENTERPRISE"
        : droneKey === "M350"
          ? "DJI M350 RTK"
          : "GNSS EMLID RS3"}
    </Typography>
    {labels.map((label, index) => {
      const item = formData.equipment[droneKey][index];
      return (
        <Paper
          key={`${droneKey}-${index}`}
          sx={{ p: 1, mb: 1, backgroundColor: "#f8f9fa" }}
        >
          <Grid container spacing={1} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2">
                {index + 1}. {label}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.status === "Ok"}
                      onChange={() =>
                        onStatusChange(
                          droneKey,
                          index,
                          "status",
                          item.status === "Ok" ? "" : "Ok"
                        )
                      }
                    />
                  }
                  label="Ok"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.status === "Not Ok"}
                      onChange={() =>
                        onStatusChange(
                          droneKey,
                          index,
                          "status",
                          item.status === "Not Ok" ? "" : "Not Ok"
                        )
                      }
                    />
                  }
                  label="Not Ok"
                />
                <TextField
                  size="small"
                  sx={{ width: 100 }}
                  placeholder="Jumlah"
                  value={item.jumlah}
                  onChange={(e) =>
                    onStatusChange(droneKey, index, "jumlah", e.target.value)
                  }
                  type="number"
                />
              </Box>
            </Grid>
            {item.status === "Not Ok" && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Keterangan"
                  value={item.keterangan}
                  onChange={(e) =>
                    onStatusChange(
                      droneKey,
                      index,
                      "keterangan",
                      e.target.value
                    )
                  }
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      );
    })}
  </Box>
);

// New Component for Pre Flight Checks
const PreFlightBlock = ({
  labels,
  checks,
  onStatusChange,
  onKeteranganChange,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6">PRE FLIGHT CHECK</Typography>
    {labels.map((label, index) => {
      const item = checks[index];
      return (
        <Paper key={index} sx={{ p: 1, mb: 1 }}>
          <Grid container alignItems="center" spacing={1}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography>
                {index + 1}. {label}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.status === "Ok"}
                      onChange={() =>
                        onStatusChange(index, item.status === "Ok" ? "" : "Ok")
                      }
                    />
                  }
                  label="Ok"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.status === "Not Ok"}
                      onChange={() =>
                        onStatusChange(
                          index,
                          item.status === "Not Ok" ? "" : "Not Ok"
                        )
                      }
                    />
                  }
                  label="Not Ok"
                />
              </Box>
            </Grid>
            {item.status === "Not Ok" && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Keterangan"
                  value={item.keterangan}
                  onChange={(e) => onKeteranganChange(index, e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      );
    })}
  </Box>
);

// New Component for Additional Information
const AdditionalInfoBlock = ({ formData, onInputChange }) => (
  <Box sx={{ mt: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
      Informasi Tambahan
    </Typography>
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label="Cuaca"
          fullWidth
          value={formData.weather}
          onChange={(e) => onInputChange("weather", e.target.value)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label="Angin"
          fullWidth
          value={formData.wind}
          onChange={(e) => onInputChange("wind", e.target.value)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label="Kondisi Pilot"
          fullWidth
          value={formData.pilotCondition}
          onChange={(e) => onInputChange("pilotCondition", e.target.value)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label="Nama Base"
          fullWidth
          value={formData.namaBase}
          onChange={(e) => onInputChange("namaBase", e.target.value)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label="Tinggi Base (cm)"
          fullWidth
          value={formData.baseHeight}
          onChange={(e) => onInputChange("baseHeight", e.target.value)}
          type="number"
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Keterangan"
          fullWidth
          multiline
          rows={3}
          value={formData.keterangan}
          onChange={(e) => onInputChange("keterangan", e.target.value)}
        />
      </Grid>
    </Grid>
  </Box>
);

export default function FormCheckListDrone() {
  const navigate = useNavigate();

  // form state
  const [selectedDrone, setSelectedDrone] = useState(["M3", "M350"]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    date: "",
    location: "",
    pilot: "",
    supervisor: "",
    weather: "",
    wind: "",
    pilotCondition: "",
    namaBase: "",
    baseHeight: "",
    keterangan: "",
    documentationPhoto: "",
    approval: "Menunggu",
    equipment: {
      M3: useInitEquipment(12),
      M350: useInitEquipment(13),
      GNSS: useInitEquipment(6),
    },
    preFlightChecks: Array.from({ length: 10 }, () => ({
      status: "",
      keterangan: "",
    })),
    fotoFile: null,
    fotoPreviewUrl: null,
  });

  // select items (could be fetched from API; currently static as in RN)
  const [pilotItems] = useState([
    "Abdul Malik",
    "Zainal Apit",
    "Gilang Vandita",
    "Taufiqurrahman",
  ]);
  const [supervisorItems] = useState(["Taufiqurrahman", "Arif Ryanto"]);

  // static lists (same content as RN)
  const equipmentListM3 = [
    "Wahana/Drone",
    "Baterei + Voltase",
    "Charging Hub/Charger Station",
    "Adaptor Charger + Kabel",
    "RTK Kit",
    "Memori Card + Kapasitas",
    "Propeler",
    "Remote Controller + Voltase",
    "Adaptor Charger + Kabel",
    "Kabel USB C - USB C",
    "Kabel Adaptor",
    "Laptop + Card Reader",
  ];
  const equipmentListM350 = [
    "Wahana/Drone",
    "Baterei + Voltase",
    "Charging Hub/Charger Station",
    "Adaptor Charger + Kabel",
    "RTK Kit",
    "Memori Card + Kapasitas",
    "Propeler",
    "Remote Controller + Voltase",
    "Adaptor Charger + Kabel",
    "Kabel USB C - USB C",
    "Kabel Adaptor",
    "Sensor LiDAR",
    "Laptop + Card Reader",
  ];
  const equipmentListGNSS = [
    "GNSS Emlid RS3",
    "Set Tribach + mini Pole",
    "Kabel USB C - USB",
    "Antenna Radio",
    "Tripod",
    "Meteran",
  ];
  const preFlightCheckItems = [
    "Setting Base (Koordinat Base, Tinggi alat, Start logging)",
    "Rencana Terbang (Area, side/overlap, Durasi, Jalur)",
    "Kondisi Airframe (Body, Propeler, Cover Kemera dilepas)",
    "Memori Card (sudah terpasang, Sisa kapasitas)",
    "Motor (Dudukan, Pengunci propeller, Normal)",
    "Baterei (Voltase, konektor bersih)",
    "Remote Controller (Baterei, antenna, Joystick)",
    "Firmware (Cek Update)",
    "Setting Kamera (ISO, Shutter Speed, Fokus)",
    "Payload Zenmuse L2 (Pemasangan OK, bersih)",
  ];

  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "info",
    buttons: [],
  });

  useEffect(() => {
    // keep pilot/supervisor copies in formData if needed (not necessary but keep)
    // (no-op here; formData is single source of truth via handlers)
  }, []);

  const showModal = (title, message, type = "info", buttons = []) => {
    setModalConfig({ title, message, type, buttons });
    setModalVisible(true);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Equipment handlers
  const handleEquipmentStatusChange = (droneKey, index, key, value) => {
    setFormData((prevFormData) => {
      const newEquipment = {
        ...prevFormData.equipment,
        [droneKey]: prevFormData.equipment[droneKey].map((item, i) =>
          i === index ? { ...item, [key]: value } : item
        ),
      };
      return {
        ...prevFormData,
        equipment: newEquipment,
      };
    });
  };

  // Preflight handlers
  const handlePreFlightCheckChange = (index, status) => {
    setFormData((prev) => {
      const newChecks = prev.preFlightChecks.map((c, i) =>
        i === index ? { ...c, status } : c
      );
      return { ...prev, preFlightChecks: newChecks };
    });
  };
  const handlePreFlightKeteranganChange = (index, text) => {
    setFormData((prev) => {
      const newChecks = prev.preFlightChecks.map((c, i) =>
        i === index ? { ...c, keterangan: text } : c
      );
      return { ...prev, preFlightChecks: newChecks };
    });
  };

  // Drone selection toggle
  const toggleDroneSelection = (drone) => {
    setSelectedDrone((prev) =>
      prev.includes(drone) ? prev.filter((d) => d !== drone) : [...prev, drone]
    );
  };

  // Image picker (web) — use input type=file
  const onPickPhoto = (file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({
      ...prev,
      fotoFile: file,
      documentationPhoto: file.name,
      fotoPreviewUrl: previewUrl,
    }));
  };

  const removePhoto = () => {
    if (formData.fotoPreviewUrl) URL.revokeObjectURL(formData.fotoPreviewUrl);
    setFormData((prev) => ({
      ...prev,
      fotoFile: null,
      documentationPhoto: "",
      fotoPreviewUrl: null,
    }));
  };

  // Validation (mirror logic from RN)
  const validateForm = () => {
    const requiredFields = [
      "location",
      "pilot",
      "supervisor",
      "weather",
      "wind",
      "pilotCondition",
      "namaBase",
      "baseHeight",
      "documentationPhoto",
    ];
    const emptyFields = requiredFields.filter(
      (f) =>
        !formData[f] ||
        (typeof formData[f] === "string" && formData[f].trim() === "")
    );
    if (emptyFields.length > 0) {
      showModal(
        "Data Belum Lengkap",
        "Harap lengkapi semua data yang wajib diisi",
        "warning"
      );
      return false;
    }

    if (selectedDrone.length === 0) {
      showModal(
        "Peringatan",
        "Pilih minimal 1 drone yang akan digunakan",
        "warning"
      );
      return false;
    }

    for (const droneType of selectedDrone) {
      const equipmentArray = formData.equipment[droneType];
      for (let i = 0; i < equipmentArray.length; i++) {
        const item = equipmentArray[i];
        if (!item.status) {
          showModal(
            "Data Belum Lengkap",
            `Status item ${i + 1} pada ${droneType} belum dipilih`,
            "warning"
          );
          return false;
        }
        if (
          item.status === "Ok" &&
          (!item.jumlah || item.jumlah.toString().trim() === "")
        ) {
          showModal(
            "Data Belum Lengkap",
            `Kolom jumlah No. ${i + 1
            } pada Drone ${droneType} harus diisi jika status "Ok".`,
            "warning"
          );
          return false;
        }
        if (
          item.status === "Not Ok" &&
          (!item.keterangan || item.keterangan.trim() === "")
        ) {
          showModal(
            "Data Belum Lengkap",
            `Kolom keterangan No. ${i + 1
            } pada Drone ${droneType} harus diisi jika status "Not Ok".`,
            "warning"
          );
          return false;
        }
      }
    }

    // GNSS always validated
    const gnssArray = formData.equipment.GNSS;
    for (let i = 0; i < gnssArray.length; i++) {
      const item = gnssArray[i];
      if (!item.status) {
        showModal(
          "Data Belum Lengkap",
          `Status item GNSS No. ${i + 1} belum dipilih.`,
          "warning"
        );
        return false;
      }
      if (
        item.status === "Ok" &&
        (!item.jumlah || item.jumlah.toString().trim() === "")
      ) {
        showModal(
          "Data Belum Lengkap",
          `Kolom jumlah GNSS No. ${i + 1} harus diisi jika status "Ok".`,
          "warning"
        );
        return false;
      }
      if (
        item.status === "Not Ok" &&
        (!item.keterangan || item.keterangan.trim() === "")
      ) {
        showModal(
          "Data Belum Lengkap",
          `Kolom keterangan GNSS No. ${i + 1
          } harus diisi jika status "Not Ok".`,
          "warning"
        );
        return false;
      }
    }

    // Preflight validation
    const incompletePreFlight = formData.preFlightChecks.some((check) => {
      return (
        !check.status ||
        (check.status === "Not Ok" &&
          (!check.keterangan || !check.keterangan.trim()))
      );
    });
    if (incompletePreFlight) {
      showModal(
        "Data Belum Lengkap",
        'Semua item Pre Flight Check harus diisi, dan keterangan wajib diisi jika "Not Ok"',
        "warning"
      );
      return false;
    }

    return true;
  };

  // Submit
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const submissionData = new FormData();

      const formatEquipmentData = (arr) =>
        arr.map((it) => ({
          status: it.status || "",
          jumlah: it.jumlah || "",
          keterangan: it.keterangan || "",
        }));

      submissionData.append("lokasi", formData.location);
      submissionData.append("pilot", formData.pilot);
      submissionData.append("supervisor", formData.supervisor);
      submissionData.append("cuaca", formData.weather);
      submissionData.append("angin", formData.wind);
      submissionData.append("kondisi_pilot", formData.pilotCondition);
      submissionData.append("nama_base", formData.namaBase);
      submissionData.append("tinggi_base", formData.baseHeight);
      submissionData.append("keterangan", formData.keterangan || "");
      submissionData.append("status", formData.approval || "Menunggu");

      submissionData.append(
        "equipment_m3",
        JSON.stringify(formatEquipmentData(formData.equipment.M3))
      );
      submissionData.append(
        "equipment_m350",
        JSON.stringify(formatEquipmentData(formData.equipment.M350))
      );
      submissionData.append(
        "equipment_gnss",
        JSON.stringify(formatEquipmentData(formData.equipment.GNSS))
      );
      submissionData.append(
        "pre_flight_checks",
        JSON.stringify(formData.preFlightChecks)
      );

      // foto
      if (formData.fotoFile) {
        submissionData.append(
          "foto_dokumentasi",
          formData.fotoFile,
          formData.fotoFile.name
        );
      }

      // selected drones (save as array or CSV)
      submissionData.append("drones", JSON.stringify(selectedDrone));

      // send via axios
      const response = await axios.post(
        `${API_URL}/drone-checklists`,
        submissionData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        showModal(
          "Berhasil!",
          "Form Checklist Drone berhasil disimpan",
          "success",
          [{ text: "OK", onPress: () => navigate(-1) }]
        );
      } else {
        throw new Error(response.data?.message || "Gagal menyimpan checklist");
      }
    } catch (error) {
      console.error("Submit error:", error);
      showModal(
        "Error",
        error.response?.data?.message ||
        error.message ||
        "Terjadi kesalahan saat menyimpan data",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const showConfirmationModal = () => {
    if (!validateForm()) return;
    showModal(
      "Konfirmasi",
      "Apakah data yang dimasukkan sudah benar?",
      "info",
      [
        { text: "Batal", onPress: () => setModalVisible(false) },
        { text: "Ya, Kirim", onPress: handleSubmit },
      ]
    );
  };

  return (
    <div className="form-container">
      <header className="app-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate("/menu")}>
            <Icon>arrow_back</Icon>
          </button>
          <h1 className="header-title"> Checklist Drone</h1>
          <div className="header-spacer"></div>
        </div>
      </header>
      <Box sx={{ maxWidth: 1000, mx: "auto", p: 2 }}>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            INFORMASI DASAR
          </Typography>
          <Grid container spacing={2} direction="column">
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Lokasi"
                fullWidth
                value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Nama Pilot"
                select
                fullWidth
                value={formData.pilot}
                onChange={(e) => handleInputChange("pilot", e.target.value)}
              >
                {pilotItems.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Supervisor"
                select
                fullWidth
                value={formData.supervisor}
                onChange={(e) =>
                  handleInputChange("supervisor", e.target.value)
                }
              >
                {supervisorItems.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">PILIH DRONE YANG DIGUNAKAN</Typography>
          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedDrone.includes("M3")}
                  onChange={() => toggleDroneSelection("M3")}
                />
              }
              label="DJI M3 Enterprise"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedDrone.includes("M350")}
                  onChange={() => toggleDroneSelection("M350")}
                />
              }
              label="DJI M350 RTK"
            />
          </Box>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">CHECKLIST PERALATAN</Typography>

          {selectedDrone.includes("M3") && (
            <EquipmentBlock
              droneKey="M3"
              labels={equipmentListM3}
              formData={formData}
              onStatusChange={handleEquipmentStatusChange}
            />
          )}
          {selectedDrone.includes("M350") && (
            <EquipmentBlock
              droneKey="M350"
              labels={equipmentListM350}
              formData={formData}
              onStatusChange={handleEquipmentStatusChange}
            />
          )}

          <EquipmentBlock
            droneKey="GNSS"
            labels={equipmentListGNSS}
            formData={formData}
            onStatusChange={handleEquipmentStatusChange}
          />
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <PreFlightBlock
            labels={preFlightCheckItems}
            checks={formData.preFlightChecks}
            onStatusChange={handlePreFlightCheckChange}
            onKeteranganChange={handlePreFlightKeteranganChange}
          />

          <AdditionalInfoBlock
            formData={formData}
            onInputChange={handleInputChange}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Foto Dokumentasi
            </Typography>
            {!formData.fotoPreviewUrl ? (
              <Box
                component="label"
                sx={{
                  border: "2px dashed",
                  borderColor: "grey.400",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "grey.600",
                  transition:
                    "border-color 0.3s, color 0.3s, background-color 0.3s",
                  "&:hover": {
                    borderColor: "primary.main",
                    color: "primary.main",
                    bgcolor: "action.hover",
                  },
                }}
              >
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={(e) => onPickPhoto(e.target.files?.[0])}
                />
                <PhotoCamera sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h6">Klik untuk Pilih Foto</Typography>
                <Typography variant="body2" color="text.secondary">
                  Atau seret dan lepas file di sini
                </Typography>
              </Box>
            ) : (
              <Box sx={{ position: "relative", mt: 2, width: "100%" }}>
                <img
                  src={formData.fotoPreviewUrl}
                  alt="Preview"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "cover",
                    borderRadius: "8px",
                    display: "block",
                  }}
                />
                <IconButton
                  onClick={removePhoto}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(255,255,255,0.8)",
                  }}
                >
                  <Delete />
                </IconButton>
              </Box>
            )}
          </Box>
        </Paper>

        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={showConfirmationModal}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
          >
            {isSubmitting ? "Menyimpan..." : "SIMPAN CHECKLIST"}
          </Button>
        </Box>

        <CustomModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
          buttons={modalConfig.buttons}
        />
      </Box>
    </div>
  );
}
